import {
  Component,
  ElementRef,
  ViewChild,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import {
  Categories,
  Departments,
  Inventory,
  LocationsModel,
  Stores,
} from '../../../../Models/ApplicationModels';
import { MasterDataSeriveService } from '../../../../Services/master-data-serive.service';
import { ExportExcelService } from '../../../../Services/export-excel.service';
import { AdminBreadcrumbsService } from '../../admin-services/admin-breadcrumbs.service';
import { debug } from 'console';
import { WeekColorService } from '../../company-config/WeekColors/services/week-color.service';
import { ProductionCycle } from '../../company-config/WeekColors/models/week-color.model';
import { OverrideStatus } from 'src/app/enum/enum';

interface SortOption {
  key: string;
  value: boolean;
}

interface FilterConfig {
  arrayKey: keyof ItemOnHandComponent;
  idKey: string;
  filterArray: number[];
}

@Component({
  selector: 'app-item-on-hand',
  templateUrl: './item-on-hand.component.html',
  styleUrls: ['./item-on-hand.component.css'],
})
export class ItemOnHandComponent implements OnInit, AfterViewInit, OnDestroy {
  // Data arrays
  inventory: Inventory[] = [];
  Deptpriceconfigarray: Inventory[] = [];
  Deptpriceconfigarray1: Inventory[] = [];
  inventoryDataForSearch: Inventory[] = [];

  // Filter arrays
  DeptpriceconfigarrayforDepartment: Inventory[] = [];
  DeptpriceconfigarrayforCategory: Categories[] = [];
  originalDeptPriceConfigArray: Categories[] = [];
  DeptpriceconfigarrayforFilters: Departments[] = [];
  DeptpriceconfigarrayforColor: ProductionCycle[] = [];
  DeptpriceconfigarrayforStore: Stores[] = [];
  DeptpriceconfigarrayforLocation: LocationsModel[] = [];

  // UI state
  filterName = 'Department';
  noData = false;
  loading = false;
  exporting = false;
  allDataLoaded = false;
  pageNumber = 1;
  pageSize = 20;

  // Filter IDs
  filterCategoriesIDs: number[] = [];
  filterColorIDs: number[] = [];
  filterStoreIDs: number[] = [];
  filterDepartmentIDs: number[] = [];
  filterLocationIDs: number[] = [];
  isFilterModalOpen: boolean = false;

  // Column visibility
  showDept = true;
  showCtg = true;
  showColor = true;
  showStore = true;
  showLocation = true;
  showInventory = true;
  showCostPrice = true;
  showCostAmt = true;
  showWeekColor: boolean = true;

  // Search
  invData = '';
  selectedDeptForFiletr: any[] = [];

  // Sort options
  arrayForsort: SortOption[] = [
    { key: 'Department', value: true },
    { key: 'Category', value: true },
    { key: 'Week Color', value: true },
    { key: 'Store', value: true },
    { key: 'Location', value: true },
    { key: 'Inventory', value: true },
    { key: 'Cost Price', value: true },
    { key: 'Cost Amt', value: true },
  ];

  @ViewChild('anchor', { static: false }) anchor!: ElementRef;
  private observer!: IntersectionObserver;

  constructor(
    private masterservice: MasterDataSeriveService,
    private exportExcelService: ExportExcelService,
    private adminBreadcrumbs: AdminBreadcrumbsService,
    private cdRef: ChangeDetectorRef,
    private weekColorService: WeekColorService
  ) { }

  ngOnInit(): void {
    const user = this.masterservice.getUserDetailsFromStorage();
    const productionScheduleType = (user?.productionScheduleType ?? '')
      .toString()
      .trim()
      .toLowerCase();
    this.showWeekColor = productionScheduleType !== 'datespecific';
    if (!this.showWeekColor) {
      this.showColor = false;
      this.filterColorIDs = [];
      this.arrayForsort = this.arrayForsort.filter((x) => x.key !== 'Week Color');
    }

    this.initializeBreadcrumbs();
    this.loadInitialData();
  }

  ngAfterViewInit(): void {
    this.setupIntersectionObserver();
  }

  ngOnDestroy(): void {
    this.cleanupIntersectionObserver();
  }

  // Initialization methods
  private initializeBreadcrumbs(): void {
    this.adminBreadcrumbs.updateBreadcrumbs([
      { crumbName: 'Inventory Management', link: 'demo' },
      { crumbName: 'Item on Hand', link: '/admin/home/onhands' },
    ]);
  }

  private loadInitialData(): void {
    this.getDepartments();
    this.getCategories();
    this.getColors();
    this.getLocations();
    this.getStores();
    this.getData();
  }

  private setupIntersectionObserver(): void {
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !this.loading && !this.allDataLoaded) {
        this.getData();
      }
    });

    if (this.anchor) {
      this.observer.observe(this.anchor.nativeElement);
    }
  }

  private cleanupIntersectionObserver(): void {
    if (this.observer && this.anchor) {
      this.observer.unobserve(this.anchor.nativeElement);
    }
  }

  // Data fetching methods
  getData(): void {
    this.fetchData(
      this.showDept,
      this.showCtg,
      this.showStore,
      this.showColor,
      this.showLocation,
      false
    );
  }

  async fetchData(
    dept: boolean,
    catg: boolean,
    store: boolean,
    color: boolean,
    location: boolean,
    tag: boolean
  ): Promise<void> {
    if (this.loading || this.allDataLoaded) return;

    this.loading = true;

    const filterRequest = this.buildFilterRequest(
      dept,
      catg,
      store,
      color,
      location
    );

    try {
      const res = await this.masterservice
        .filterJournal(filterRequest)
        .toPromise();
      const response = res as Inventory[];

      if (response.length < this.pageSize && this.pageNumber > 1) {
        this.allDataLoaded = true;
      } else {
        this.pageNumber++;
      }

      this.updateDataArrays(response);
    } finally {
      this.loading = false;
    }
  }

  /** Single definition of the active filters, shared by the paged grid fetch and the export. */
  private buildFilterRequest(
    dept: boolean,
    catg: boolean,
    store: boolean,
    color: boolean,
    location: boolean
  ): any {
    const company = this.masterservice.getUserDetailsFromStorage();

    return {
      includeDepartment: dept,
      departmentIds: this.filterDepartmentIDs.join(','),
      includeCategory: catg,
      categoryIds: this.filterCategoriesIDs.join(','),
      includeStore: store,
      storeIds: this.filterStoreIDs.join(','),
      includeColor: color,
      colorIds: this.filterColorIDs.join(','),
      includeLocation: location,
      locationIds: this.filterLocationIDs.join(','),
      includeBarcode: false,
      companyID: company.companyID,
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      isExport: false,
    };
  }

  private updateDataArrays(response: Inventory[]): void {
    this.inventoryDataForSearch = [...this.inventoryDataForSearch, ...response];
    this.Deptpriceconfigarray = [...this.Deptpriceconfigarray, ...response];
    this.Deptpriceconfigarray1 = [...this.Deptpriceconfigarray1, ...response];

    this.sortDepartmentArray();
    this.cdRef.detectChanges();
  }

  private sortDepartmentArray(): void {
    this.Deptpriceconfigarray.sort((a, b) => {
      const descrA = a.departmentDescr?.toLowerCase() || '';
      const descrB = b.departmentDescr?.toLowerCase() || '';
      return descrA.localeCompare(descrB);
    });
  }

  // Filter methods
  collectFilterParameters(
    selectedId: number,
    arrayKey: keyof ItemOnHandComponent,
    idKey: string,
    filterArray: number[]
  ): void {
    const array = this[arrayKey] as any[];
    const item = array.find((x) => x[idKey] === selectedId);

    if (item) {
      item.isSelected = !item.isSelected;
      this.updateFilterArray(filterArray, item[idKey], item.isSelected);
    }

    if (this.filterDepartmentIDs.length > 0) {
      this.DeptpriceconfigarrayforCategory = this.originalDeptPriceConfigArray.filter(
        (cat) => this.filterDepartmentIDs.includes(cat.departmentId)
      );
    } else {
      this.DeptpriceconfigarrayforCategory = [...this.originalDeptPriceConfigArray];
    }
  }

  private updateFilterArray(
    filterArray: number[],
    id: number,
    isSelected: boolean
  ): void {
    const index = filterArray.indexOf(id);

    if (isSelected && index === -1) {
      filterArray.push(id);
    } else if (!isSelected && index > -1) {
      filterArray.splice(index, 1);
    }
  }

  getFilterData(): void {
    this.pageNumber = 1; // Reset to first page
    this.allDataLoaded = false; // Reset loaded flag
    this.inventoryDataForSearch = []; // Clear existing data
    this.Deptpriceconfigarray = [];
    this.Deptpriceconfigarray1 = [];
    this.cdRef.detectChanges(); // Trigger change detection

    // Force a new request by calling getData()
    this.getData();
  }

  clearFilter(filterType: string): void {
    switch (filterType) {
      case 'Department':
        this.clearFilterArray(
          this.filterDepartmentIDs,
          this.DeptpriceconfigarrayforDepartment
        );
        break;
      case 'Category':
        this.clearFilterArray(
          this.filterCategoriesIDs,
          this.DeptpriceconfigarrayforCategory
        );
        break;
      case 'Color':
        this.clearFilterArray(
          this.filterColorIDs,
          this.DeptpriceconfigarrayforColor
        );
        break;
      case 'Store':
        this.clearFilterArray(
          this.filterStoreIDs,
          this.DeptpriceconfigarrayforStore
        );
        break;
      case 'Location':
        this.clearFilterArray(
          this.filterLocationIDs,
          this.DeptpriceconfigarrayforLocation
        );
        break;
    }

    this.noData = false;
    this.getFilterData();
  }

  private clearFilterArray(filterArray: number[], dataArray: any[]): void {
    filterArray.length = 0;
    dataArray.forEach((item) => (item.isSelected = false));
  }

  // Search methods
  search(): void {
    if (!this.invData) {
      this.Deptpriceconfigarray = [...this.inventoryDataForSearch];
      return;
    }

    const searchTerm = this.invData.toLowerCase();
    this.Deptpriceconfigarray = this.inventoryDataForSearch.filter(
      (item) =>
        item.departmentDescr?.toLowerCase().includes(searchTerm) ||
        item.categoryName?.toLowerCase().includes(searchTerm)
    );
  }

  getSearchResult(event: any): void {
    if (!this.invData) {
      this.Deptpriceconfigarray = [...this.inventoryDataForSearch];
      return;
    }

    if (event.keyCode !== 8) {
      this.search();
    } else {
      this.Deptpriceconfigarray = [...this.inventoryDataForSearch];
      this.search();
    }
  }

  // Master data fetching methods
  getDepartments(): void {
    this.masterservice.getMainDepartments().subscribe((res) => {
      this.DeptpriceconfigarrayforFilters = res as Departments[];
    });
  }

  getCategories(): void {
    const user = this.masterservice.getUserDetailsFromStorage();
    this.masterservice.getCategoryAdmin(user.companyID).subscribe((res) => {
      this.DeptpriceconfigarrayforCategory = res as Categories[];
      this.originalDeptPriceConfigArray = res as Categories[];
    });
  }

  getColors(): void {
    const user = this.masterservice.getUserDetailsFromStorage();
    this.weekColorService.getProductionCycles(user.companyID).subscribe((res) => {
      this.DeptpriceconfigarrayforColor = (res as ProductionCycle[]).filter(
        (item) => item.status === OverrideStatus.Active
      );

    }
    );
  }

  getStores(): void {
    const user = this.masterservice.getUserDetailsFromStorage();
    this.masterservice.getallstores(user.companyID).subscribe((res) => {
      this.DeptpriceconfigarrayforStore = res as Stores[];
    });
  }

  getLocations(): void {
    const userDetails = this.masterservice.getUserDetailsFromStorage();
    this.masterservice.getWarehouseLocations(userDetails.companyID, null).subscribe({
      next: (res: any) => {
        if (res) {
          this.DeptpriceconfigarrayforLocation = res as LocationsModel[];
        } else {
          console.warn('No location items found in response', res);
          this.DeptpriceconfigarrayforLocation = [];
        }
      },
      error: (error) => {
        console.error('Error fetching locations:', error);
        this.DeptpriceconfigarrayforLocation = [];
      },
    });
  }

  // UI interaction methods
  selectedFilterCategory(filterType: string): void {
    this.filterName = filterType;
  }

  openFilterModal(modalState: string): void {
    if (modalState === 'Open') {
      this.isFilterModalOpen = true;
    }

    if (modalState === 'Close') {
      this.isFilterModalOpen = false;

      // Clear filter ID arrays
      this.filterDepartmentIDs = [];
      this.filterCategoriesIDs = [];
      this.filterColorIDs = [];
      this.filterStoreIDs = [];
      this.filterLocationIDs = [];

      // Reset selection flags on filter arrays
      (this.DeptpriceconfigarrayforFilters || []).forEach((el: any) => (el.isSelected = false));
      (this.DeptpriceconfigarrayforCategory || []).forEach((el: any) => (el.isSelected = false));
      (this.DeptpriceconfigarrayforColor || []).forEach((el: any) => (el.isSelected = false));
      (this.DeptpriceconfigarrayforStore || []).forEach((el: any) => (el.isSelected = false));
      (this.DeptpriceconfigarrayforLocation || []).forEach((el: any) => (el.isSelected = false));
    }
  }

  showhide(index: number): void {
    switch (index) {
      case 0:
        this.showDept = !this.showDept;
        break;
      case 1:
        this.showCtg = !this.showCtg;
        break;
      case 2:
        this.showColor = !this.showColor;
        break;
      case 3:
        this.showStore = !this.showStore;
        break;
      case 4:
        this.showLocation = !this.showLocation;
        break;
      case 5:
        this.showInventory = !this.showInventory;
        break;
      case 6:
        this.showCostPrice = !this.showCostPrice;
        break;
      case 7:
        this.showCostAmt = !this.showCostAmt;
        break;
    }
  }

  // Export methods
  async exportToExcel(): Promise<void> {
    if (this.exporting) return;

    this.exporting = true;
    try {
      // The grid is paged via infinite scroll, so it only holds the pages loaded so far.
      // Ask the API for the full filtered set instead of exporting what is in memory.
      const request = this.buildFilterRequest(
        this.showDept,
        this.showCtg,
        this.showStore,
        this.showColor,
        this.showLocation
      );
      request.isExport = true;

      const res = await this.masterservice.filterJournal(request).toPromise();
      const rows = this.buildExportRows((res as Inventory[]) ?? []);

      this.exportExcelService.exportAsExcelFile(rows, 'Items-on-hands');
    } finally {
      this.exporting = false;
      this.cdRef.detectChanges();
    }
  }

  private buildExportRows(items: Inventory[]): any[] {
    return items.map((item) => ({
      Department: item.departmentDescr,
      Category: item.categoryName,
      ...(this.showWeekColor ? { WeekColor: item.displayName } : {}),
      Store: item.storeName,
      Location: item.locationTypeDesc,
      ItemOnHands: item.qty,
      CostPrice: this.formatCurrency(item.totalPrice / item.qty),
      CostAmount: this.formatCurrency(item.totalPrice),
    }));
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  }

  // Utility methods
  removeTimeFromDate(dateString: string | null | undefined): string {
    return dateString?.split(' ')[0] || '';
  }
}
